import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { getPlural } from 'lib/string/pluralize'

/**
 * Returns an ICU-pluralized "count + saleUnit" string (e.g. "2 rooms", "1 villa")
 * for all known sale unit values, with a getPlural fallback for unknown units.
 *
 * Use the hook version (`useSaleUnitPlural`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function formatSaleUnitCount(intl: IntlShape, saleUnit: string, count: number): string {
  const key = saleUnit.toLowerCase()
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  let result: string | undefined

  if (key === 'room') {
    result = intl.formatMessage({
      id: 'xIOgTnhCny',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Sale unit count for rooms',
    }, { count })
  } else if (key === 'night') {
    result = intl.formatMessage({
      id: 'CrXc7nVuf3',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Sale unit count for nights',
    }, { count })
  } else if (key === 'villa') {
    result = intl.formatMessage({
      id: 'YbsPL43fxT',
      defaultMessage: '{count, plural, one {# villa} other {# villas}}',
      description: 'Sale unit count for villas',
    }, { count })
  } else if (key === 'suite') {
    result = intl.formatMessage({
      id: 'Uc/eosLHUi',
      defaultMessage: '{count, plural, one {# suite} other {# suites}}',
      description: 'Sale unit count for suites',
    }, { count })
  } else if (key === 'guest') {
    result = intl.formatMessage({
      id: 'M4jSAXnKcL',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Sale unit count for guests',
    }, { count })
  } else if (key === 'adult') {
    result = intl.formatMessage({
      id: 'XxfuCwJBR2',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Sale unit count for adults',
    }, { count })
  } else if (key === 'traveller') {
    result = intl.formatMessage({
      id: 'ldCOot5iuW',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
      description: 'Sale unit count for travellers',
    }, { count })
  } else if (key === 'person') {
    result = intl.formatMessage({
      id: 'O5OFS0v7aP',
      defaultMessage: '{count, plural, one {# person} other {# people}}',
      description: 'Sale unit count for people',
    }, { count })
  } else if (key === 'cabin') {
    result = intl.formatMessage({
      id: 'YYw6RUm2On',
      defaultMessage: '{count, plural, one {# cabin} other {# cabins}}',
      description: 'Sale unit count for cabins',
    }, { count })
  } else if (key === 'package') {
    result = intl.formatMessage({
      id: '2EjbAM5bYA',
      defaultMessage: '{count, plural, one {# package} other {# packages}}',
      description: 'Sale unit count for packages',
    }, { count })
  } else if (key === 'property') {
    result = intl.formatMessage({
      id: 'QuupZrsRjc',
      defaultMessage: '{count, plural, one {# property} other {# properties}}',
      description: 'Sale unit count for properties',
    }, { count })
  }

  if (result) {
    if (startsWithCapital) {
      // Preserve capitalization of the unit word (e.g. "2 rooms" → "2 Rooms")
      const spaceIdx = result.indexOf(' ')
      if (spaceIdx !== -1 && spaceIdx + 1 < result.length) {
        return result.slice(0, spaceIdx + 1) + result[spaceIdx + 1].toUpperCase() + result.slice(spaceIdx + 2)
      }
    }
    return result
  }

  // i18n: fallback for unknown sale units — uses getPlural (English-only)
  return intl.formatMessage({
    id: 'FLCql9MZpV',
    defaultMessage: '{count, plural, one {# {saleUnit}} other {# {saleUnitPlural}}}',
    description: 'Fallback sale unit count for unrecognized sale units',
  }, { count, saleUnit, saleUnitPlural: getPlural(saleUnit) })
}

/**
 * Returns the plural noun for a known sale unit (e.g. "rooms", "villas", "people").
 * Falls back to getPlural for unknown units.
 *
 * Use this when you need just the word without a count, e.g. "Select your rooms".
 */
export function getSaleUnitPluralNoun(saleUnit: string): string {
  const known: Record<string, string> = {
    room: 'rooms',
    night: 'nights',
    villa: 'villas',
    suite: 'suites',
    guest: 'guests',
    adult: 'adults',
    traveller: 'travellers',
    person: 'people',
    cabin: 'cabins',
    package: 'packages',
    property: 'properties',
  }
  const result = known[saleUnit.toLowerCase()]
  if (result) {
    const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase()
    if (startsWithCapital) {
      return result.charAt(0).toUpperCase() + result.slice(1)
    }
    return result
  }
  return getPlural(saleUnit)
}

interface SaleUnitPluralFns {
  formatSaleUnitCount: (saleUnit: string, count: number) => string
  getSaleUnitPluralNoun: (saleUnit: string) => string
}

function useSaleUnitPlural(): SaleUnitPluralFns {
  const intl = useIntl()
  return useMemo(() => ({
    formatSaleUnitCount: (saleUnit: string, count: number) => formatSaleUnitCount(intl, saleUnit, count),
    getSaleUnitPluralNoun,
  }), [intl])
}

export default useSaleUnitPlural
